import React from "react";
import { Shield, Clock, CheckCircle2, Archive, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";

const STATUS_LABELS = {
  new: { label: "Ny", color: "bg-red-100 text-red-900 border-red-300" },
  reviewing: { label: "Under behandling", color: "bg-amber-100 text-amber-900 border-amber-300" },
  resolved: { label: "Løst", color: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  archived: { label: "Arkiveret", color: "bg-slate-100 text-slate-700 border-slate-300" },
};

const CATEGORY_LABELS = {
  harassment: "Chikane/diskrimination",
  fraud: "Bedrageri",
  safety: "Sikkerhedsbrud",
  data: "GDPR-brud",
  corruption: "Korruption",
  other: "Andet",
  general: "Generelt",
};

export default function WhistleblowerPanel() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);
  const [updating, setUpdating] = React.useState(false);
  const [adminNote, setAdminNote] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/whistleblower");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Kunne ikke hente indberetninger");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    setUpdating(true);
    try {
      await adminApi().patch(`/admin/whistleblower/${id}`, {
        status,
        admin_note: adminNote || undefined,
      });
      toast.success("Status opdateret");
      setSelected(null);
      setAdminNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke opdatere");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <div className="bg-white border border-brand p-6 text-sm text-brand-text2" data-testid="whistleblower-loading">Indlæser indberetninger…</div>;
  }

  const newCount = items.filter((i) => i.status === "new").length;

  return (
    <div className="space-y-4" data-testid="whistleblower-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-2xl text-brand-dark flex items-center gap-2">
            <Shield size={22} className="text-brand-red" />
            Whistleblower-indberetninger
          </h2>
          <p className="text-sm text-brand-text2 mt-1">
            Anonyme indberetninger fra emakurer.dk/whistleblower.
            {newCount > 0 && <span className="ml-2 text-red-700 font-bold">· {newCount} nye</span>}
          </p>
        </div>
      </div>

      {items.length === 0 && (
        <div className="bg-white border border-brand p-8 text-center text-brand-text2 text-sm">
          Ingen indberetninger endnu. Formularen ligger på <code className="text-brand-red">/whistleblower</code> og sender også notifikation til <code className="text-brand-red">whistleblower@emakurer.dk</code>.
        </div>
      )}

      {items.map((item) => {
        const st = STATUS_LABELS[item.status] || STATUS_LABELS.new;
        return (
          <div
            key={item.id}
            className="bg-white border border-brand p-5 hover:shadow-sm transition-shadow"
            data-testid={`whistleblower-item-${item.id}`}
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 border ${st.color} font-bold uppercase`}>{st.label}</span>
                  <span className="text-xs text-brand-text2">{CATEGORY_LABELS[item.category] || item.category}</span>
                  <span className="text-xs text-brand-text2">·</span>
                  <span className="text-xs text-brand-text2">{(item.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  {item.anonymous ? (
                    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 border border-slate-300">Anonym</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-900 border border-blue-300">Med kontakt</span>
                  )}
                </div>
                <h3 className="font-display font-bold text-base text-brand-dark mb-2">{item.subject}</h3>
                <p className="text-sm text-brand-text2 line-clamp-2">{item.message?.slice(0, 240)}{item.message?.length > 240 ? "…" : ""}</p>
                {!item.anonymous && (item.contact_email || item.contact_phone) && (
                  <div className="mt-2 text-xs text-brand-text2">
                    Kontakt: {item.contact_email && <a href={`mailto:${item.contact_email}`} className="text-brand-red">{item.contact_email}</a>}
                    {item.contact_email && item.contact_phone && " · "}
                    {item.contact_phone && <span>{item.contact_phone}</span>}
                  </div>
                )}
              </div>
              <button
                onClick={() => { setSelected(item); setAdminNote(item.admin_note || ""); }}
                className="text-xs border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white px-3 py-2 flex items-center gap-1 flex-shrink-0 transition-colors"
                data-testid={`whistleblower-open-${item.id}`}
              >
                <Eye size={12} />
                Åbn
              </button>
            </div>
          </div>
        );
      })}

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
          onClick={() => setSelected(null)}
          data-testid="whistleblower-modal"
        >
          <div
            className="bg-white border border-brand-dark max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-brand-dark text-white p-5 sticky top-0">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="kicker text-white/70 mb-1">{CATEGORY_LABELS[selected.category] || selected.category}</div>
                  <h2 className="font-display font-bold text-xl">{selected.subject}</h2>
                </div>
                <button onClick={() => setSelected(null)} className="text-white/80 hover:text-white text-2xl leading-none" data-testid="whistleblower-modal-close">×</button>
              </div>
              <div className="text-xs text-white/60 mt-2">
                ID: <code className="font-mono">{selected.id}</code> · Modtaget {selected.created_at?.slice(0, 19).replace("T", " ")}
              </div>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <div className="kicker mb-2">Indhold</div>
                <div className="bg-brand-bg2 border border-brand p-4 text-sm whitespace-pre-wrap" data-testid="whistleblower-modal-message">
                  {selected.message}
                </div>
              </div>

              {!selected.anonymous && (selected.contact_email || selected.contact_phone) && (
                <div className="bg-blue-50 border border-blue-300 p-4">
                  <div className="kicker text-blue-900 mb-2">Indberetter har givet kontakt</div>
                  {selected.contact_email && <div className="text-sm">Email: <a href={`mailto:${selected.contact_email}`} className="text-brand-red">{selected.contact_email}</a></div>}
                  {selected.contact_phone && <div className="text-sm">Telefon: {selected.contact_phone}</div>}
                </div>
              )}

              <div>
                <div className="kicker mb-2">Admin-note (intern)</div>
                <textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  rows={3}
                  className="w-full border border-brand p-3 text-sm font-sans"
                  placeholder="Noter til intern brug — fx besluttede handlinger"
                  data-testid="whistleblower-admin-note"
                />
              </div>

              {selected.read_by && (
                <div className="text-xs text-brand-text2">
                  Senest håndteret af {selected.read_by} ({selected.read_at?.slice(0, 19).replace("T", " ")})
                </div>
              )}

              <div className="flex flex-wrap gap-2 border-t border-brand pt-4">
                <button
                  onClick={() => updateStatus(selected.id, "reviewing")}
                  disabled={updating}
                  className="text-xs bg-amber-600 text-white hover:bg-amber-700 px-4 py-2 flex items-center gap-1 disabled:opacity-60"
                  data-testid="whistleblower-status-reviewing"
                >
                  <Clock size={12} /> Under behandling
                </button>
                <button
                  onClick={() => updateStatus(selected.id, "resolved")}
                  disabled={updating}
                  className="text-xs bg-emerald-600 text-white hover:bg-emerald-700 px-4 py-2 flex items-center gap-1 disabled:opacity-60"
                  data-testid="whistleblower-status-resolved"
                >
                  <CheckCircle2 size={12} /> Markér som løst
                </button>
                <button
                  onClick={() => updateStatus(selected.id, "archived")}
                  disabled={updating}
                  className="text-xs bg-slate-600 text-white hover:bg-slate-700 px-4 py-2 flex items-center gap-1 disabled:opacity-60"
                  data-testid="whistleblower-status-archived"
                >
                  <Archive size={12} /> Arkivér
                </button>
                {updating && <Loader2 size={14} className="animate-spin text-brand-dark ml-2" />}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
